import CustomStore from 'devextreme/data/custom_store';
import EventBus from '@/services/event-bus';
import Api from '../../../services/Api';
import { applySamplesExportPagination } from '@/utils/samplesExport';
import {
  compareResultSortMeta,
  getResultSortMeta,
} from '@/utils/resultSort';

let lastFoodSamplesReq = null;
let lastFoodSamplesWithFiltersReq = null;
let lastFoodSampleDetailsReq = null;
let lastFoodSampleDetailsWithFiltersReq = null;

const DETAIL_SORT_SELECTOR_MAP = {
  result_value: 'value__ResultadoPresentacion',
  unit_value: 'value__unit',
  range_value: 'value__r_min',
  cma_value: 'cma__CMASuperior',
};

const LOCAL_DETAIL_SORT_SELECTORS = [
  'result_value',
  'unit_value',
  'range_value',
  'cma_value',
  'value__ResultadoPresentacion',
  'value__unit',
  'value__r_min',
  'cma__CMASuperior',
];

function isLocalDetailSortSelector(selector) {
  return (
    LOCAL_DETAIL_SORT_SELECTORS.includes(selector) ||
    /^lmr\[\d+\]\.Valor$/.test(selector)
  );
}

function normalizeDetailSortSettings(sortSettings = []) {
  return sortSettings.map((sortSetting) => ({
    ...sortSetting,
    selector:
      DETAIL_SORT_SELECTOR_MAP[sortSetting.selector] ?? sortSetting.selector,
  }));
}

function getLocalDetailSortValue(rowData, selector) {
  const lmrMatch = String(selector).match(/^lmr\[(\d+)\]\.Valor$/);

  if (lmrMatch) {
    const lmrIndex = Number(lmrMatch[1]);
    const value = rowData?.lmr?.[lmrIndex]?.Valor;

    const numericValue = Number(
      String(value ?? '').replace(',', '.')
    );

    return Number.isNaN(numericValue)
      ? String(value ?? '')
      : numericValue;
  }

  switch (selector) {
    case 'result_value':
    case 'value__ResultadoPresentacion':
      return getResultSortMeta(
        rowData?.value?.ResultadoPresentacion
      );
    case 'unit_value':
    case 'value__unit':
      return rowData?.value?.unit ?? '';
    case 'range_value':
    case 'value__r_min':
      return Number(
        rowData?.value?.r_min ?? Number.NEGATIVE_INFINITY
      );
    case 'cma_value':
    case 'cma__CMASuperior':
      return rowData?.cma?.[0]?.CMASuperior ?? '';
    default:
      return rowData?.[selector] ?? '';
  }
}

function sortLocalDetailRows(rows, sortSettings) {
  if (
    !Array.isArray(rows) ||
    rows.length === 0 ||
    !sortSettings?.length
  ) {
    return rows;
  }

  const rowsCopy = [...rows];
  rowsCopy.sort((leftRow, rightRow) => {
    for (const sortSetting of sortSettings) {
      const leftValue = getLocalDetailSortValue(
        leftRow,
        sortSetting.selector
      );
      const rightValue = getLocalDetailSortValue(
        rightRow,
        sortSetting.selector
      );

      const comparison = compareResultSortMeta(
        leftValue,
        rightValue,
        sortSetting.desc
      );

      if (comparison !== 0) {
        return comparison;
      }
    }

    return 0;
  });

  return rowsCopy;
}

export default {
  namespaced: true,
  state: {
    allSamples: [],
    sampleDetails: null,
    isNoRp: false,
    europeanChains: [],
    isExporting: false,
  },
  actions: {
    async getSamples({ rootState }, params) {
      try {
        if (lastFoodSamplesReq && lastFoodSamplesReq.abort) {
          lastFoodSamplesReq.abort();
        }

        lastFoodSamplesReq = Api.getFoodSamples(
          params,
          rootState.contact.company,
          rootState.contact.address
        );

        const xhr = await lastFoodSamplesReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({ data: [], totalCount: 0 });
        }
        return Promise.reject(error);
      }
    },
    async getSamplesWithFilters({ rootState }, params) {
      try {
        if (
          lastFoodSamplesWithFiltersReq &&
          lastFoodSamplesWithFiltersReq.abort
        ) {
          lastFoodSamplesWithFiltersReq.abort();
        }

        lastFoodSamplesWithFiltersReq = Api.getFoodSamplesWithFilters(
          params,
          rootState.contact.company,
          rootState.contact.address
        );

        const xhr = await lastFoodSamplesWithFiltersReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({ data: [], totalCount: 0 });
        }
        return Promise.reject(error);
      }
    },
    async getSampleDetails({ rootState }, { id, params }) {
      try {
        if (
          lastFoodSampleDetailsReq &&
          lastFoodSampleDetailsReq.abort
        ) {
          lastFoodSampleDetailsReq.abort();
        }

        lastFoodSampleDetailsReq = Api.getSampleDetails(
          id,
          params,
          null,
          rootState.contact.company,
          rootState.contact.address
        );
        const xhr = await lastFoodSampleDetailsReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({
            data: [],
            totalCount: 0,
          });
        }

        return Promise.resolve({
          data: [],
          totalCount: 0,
        });
      }
    },
    async getSampleDetailsWithFilters({ rootState }, data) {
      try {
        if (
          lastFoodSampleDetailsWithFiltersReq &&
          lastFoodSampleDetailsWithFiltersReq.abort
        ) {
          lastFoodSampleDetailsWithFiltersReq.abort();
        }

        lastFoodSampleDetailsWithFiltersReq =
          Api.getSamplesDetailsWithFilters(
            data.params,
            data.id,
            rootState.contact.company,
            rootState.contact.address
          );
        const xhr = await lastFoodSampleDetailsWithFiltersReq;
        const response = JSON.parse(xhr.response);

        return Promise.resolve({
          data: response.data,
          totalCount: response.totalCount,
        });
      } catch (error) {
        if (error && error.message === 'abort') {
          return Promise.resolve({
            data: [],
            totalCount: 0,
          });
        }

        return Promise.resolve({
          data: [],
          totalCount: 0,
        });
      }
    },
    getStore({ dispatch, rootState }) {
      return new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = normalizeDetailSortSettings(loadOptions[i]);
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);
          let subsistuteParams = params.replace('skip', 'offset');
          subsistuteParams = subsistuteParams.replace(
            'take',
            'limit'
          );

          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: false,
            },
            { root: true }
          );

          subsistuteParams += sortParams;

          if (!subsistuteParams.startsWith('?')) {
            subsistuteParams = `?${subsistuteParams}`;
          }
          if (loadOptions.filter) {
            if (loadOptions.filter[0]) {
              subsistuteParams += `&filter_search=${loadOptions.filter[0].filterValue}`;
            }
          }
          return dispatch(
            'getSamples',
            applySamplesExportPagination(subsistuteParams, loadOptions)
          );
        },
      });
    },
    getDetailsStore({ dispatch, rootState, state, commit }) {
      return new CustomStore({
        key: 'parameter_id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [
            { selector: 'group_order', desc: false },
            // { selector: 'parameter_order', desc: false },
            { selector: 'parameter', desc: false },
          ];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = normalizeDetailSortSettings(loadOptions[i]);
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);
          let subsistuteParams = params.replace('skip', 'offset');
          subsistuteParams = subsistuteParams.replace(
            'take',
            'limit'
          );
          if (state.isExporting) {
            subsistuteParams += '&all=true';
          }
          const hasLocalSort = sortSettings.some((sortSetting) =>
            isLocalDetailSortSelector(sortSetting.selector)
          );
          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: false,
            },
            { root: true }
          );

          if (!hasLocalSort) {
            subsistuteParams += sortParams;
          }

          if (!subsistuteParams.startsWith('?')) {
            subsistuteParams = `?${subsistuteParams}`;
          }

          let searchValue = null;

          if (loadOptions.searchValue) {
            searchValue = loadOptions.searchValue;
          } else if (
            Array.isArray(loadOptions.filter) &&
            loadOptions.filter?.[0]?.filterValue != null
          ) {
            searchValue = loadOptions.filter[0].filterValue;
          } else if (
            Array.isArray(loadOptions.filter) &&
            typeof loadOptions.filter[2] !== 'undefined'
          ) {
            searchValue = loadOptions.filter[2];
          }

          if (searchValue) {
            subsistuteParams += `&filter_search=${encodeURIComponent(
              searchValue
            )}`;
          }

          commit('setIsExporting', false);

          if (hasLocalSort) {
            const allRowsResponse = await dispatch('getSampleDetails', {
              id: rootState.filter.currentSampleId.toString(),
              params: `${subsistuteParams}${
                subsistuteParams.includes('?') ? '&' : '?'
              }all=true`,
            });

            const sortedRows = sortLocalDetailRows(
              allRowsResponse.data,
              sortSettings
            );
            const offset = loadOptions.skip ?? 0;
            const limit = loadOptions.take ?? sortedRows.length;

            return {
              data: sortedRows.slice(offset, offset + limit),
              totalCount: sortedRows.length,
            };
          }

          return dispatch('getSampleDetails', {
            id: rootState.filter.currentSampleId.toString(),
            params: subsistuteParams,
          });
        },
      });
    },
    async getStoreWithFilters({ rootState, dispatch }, paramFilters) {
      return new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = normalizeDetailSortSettings(loadOptions[i]);
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          params = params.slice(0, -1);

          if (params.includes('skip')) {
            let subsistuteParams = params.replace('skip', 'offset');

            subsistuteParams = subsistuteParams.replace(
              'take',
              'limit'
            );

            const offsetLimit = subsistuteParams.split('&');

            const offset = offsetLimit[0].split('=')[1];

            const limit = offsetLimit[1].split('=')[1];
            paramFilters.offset = offset;
            paramFilters.limit = limit;
          } else {
            paramFilters.limit = null;
            paramFilters.offset = null;
          }

          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: true,
            },
            { root: true }
          );

          if (loadOptions.filter) {
            if (loadOptions.filter[0]) {
              paramFilters.filter_search =
                loadOptions.filter[0].filterValue;
            }
          } else {
            paramFilters.filter_search = null;
          }

          paramFilters.ordering = sortParams.sort;
          const requestFilters =
            loadOptions.isLoadingAll === true
              ? { ...paramFilters, offset: 0, limit: 250 }
              : paramFilters;
          return dispatch('getSamplesWithFilters', requestFilters);
        },
      });
    },
    async getStoreDetailsWithFilters(
      { dispatch },
      { id, paramFilters }
    ) {
      return new CustomStore({
        key: 'parameter_id',
        load: async (loadOptions) => {
          let params = '?';
          let sortSettings = [];

          ['skip', 'take', 'sort'].forEach((i) => {
            if (i in loadOptions) {
              if (i === 'sort' && loadOptions[i] !== null) {
                sortSettings = loadOptions[i];
              } else {
                params += `${i}=${JSON.stringify(loadOptions[i])}&`;
              }
            }
          });

          if (params.includes('skip')) {
            let subsistuteParams = params.replace('skip', 'offset');

            subsistuteParams = subsistuteParams.replace(
              'take',
              'limit'
            );

            if (!subsistuteParams) return [];

            const offsetLimit = subsistuteParams.split('&');

            const offset = offsetLimit[0].split('=')[1];

            const limit = offsetLimit[1].split('=')[1];
            paramFilters.offset = offset;
            paramFilters.limit = limit;
          } else {
            paramFilters.limit = null;
            paramFilters.offset = null;
          }

          const sortParams = await dispatch(
            'filter/applySort',
            {
              sortSettings,
              isFilter: true,
            },
            { root: true }
          );

          let searchValue = null;

          if (loadOptions.searchValue) {
            searchValue = loadOptions.searchValue;
          } else if (
            Array.isArray(loadOptions.filter) &&
            loadOptions.filter?.[0]?.filterValue != null
          ) {
            searchValue = loadOptions.filter[0].filterValue;
          } else if (
            Array.isArray(loadOptions.filter) &&
            typeof loadOptions.filter[2] !== 'undefined'
          ) {
            searchValue = loadOptions.filter[2];
          }

          paramFilters.filter_search = searchValue || null;

          const hasLocalSort = sortSettings.some((sortSetting) =>
            isLocalDetailSortSelector(sortSetting.selector) ||
              ['parameter', 'parameter_group'].includes(
                sortSetting.selector
              )
          );

          if (hasLocalSort) {
            paramFilters.offset = null;
            paramFilters.limit = null;
            paramFilters.ordering = null;
            paramFilters.all = true;

            const allRowsResponse = await dispatch(
              'getSampleDetailsWithFilters',
              {
                id,
                params: paramFilters,
              }
            );

            const sortedRows = sortLocalDetailRows(
              allRowsResponse.data,
              sortSettings
            );
            const offset = loadOptions.skip ?? 0;
            const limit = loadOptions.take ?? sortedRows.length;

            return {
              data: sortedRows.slice(offset, offset + limit),
              totalCount: sortedRows.length,
            };
          }

          paramFilters.all = null;
          paramFilters.ordering = sortParams.sort;

          return dispatch('getSampleDetailsWithFilters', {
            id,
            params: paramFilters,
          });
        },
      });
    },
    async getFoodChains({ rootState }) {
      try {
        const xhr = await Api.getSatelliteFoodChains(
          rootState.contact.company,
          rootState.contact.address
        );
        const response = JSON.parse(xhr.response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async getParameterLMRs({ rootState }) {
      try {
        const xhr = await Api.getSampleParameterLMRs(
          rootState.filter.currentSampleId,
          rootState.contact.company,
          rootState.contact.address
        );
        const response = JSON.parse(xhr.response);

        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    callStoreWithFilters({ commit }, params) {
      EventBus.$emit('FoodSamplesPage', params);
    },
    callStoreDetailsWithfilters({ commit }, { id, params }) {
      EventBus.$emit('foodDetailsFilteredSamples', {
        id,
        params,
      });
    },
    setFilteredSamples({ commit }, value) {
      EventBus.$emit('FoodSamplesPage', null);
    },
    setFilteredSampleDetails({ commit }, value) {
      EventBus.$emit('foodDetailsFilteredSamples', {
        id: null,
        params: null,
      });
    },
    setSamples({ commit }, value) {
      commit('setSamples', value);
    },
    setSampleDetails({ commit }, value) {
      commit('setSampleDetails', value);
    },
    setIsNoRp({ commit }, value) {
      commit('setIsNoRp', value);
    },
    setEuropeanChains({ commit }, value) {
      commit('setEuropeanChains', value);
    },
    setIsExporting({ commit }, value) {
      commit('setIsExporting', value);
    },
  },
  mutations: {
    setSamples(state, value) {
      state.allSamples = value;
    },
    setSampleDetails(state, value) {
      state.sampleDetails = value;
    },
    setIsNoRp(state, value) {
      state.isNoRp = value;
    },
    setEuropeanChains(state, value) {
      state.europeanChains = value;
    },
    setIsExporting(state, value) {
      state.isExporting = value;
    },
  },
};
